import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Clock, CheckCircle, XCircle, Loader2, Trophy } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ExamPage = () => {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const { token, loading: authLoading } = useAuth();
  
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(12 * 60); // 12 minutes in seconds
  const handleSubmitRef = useRef(null);

  const fetchExam = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/exams/${schoolId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to load exam');
      }
      
      const data = await response.json();
      setExam(data);
      setTimeLeft(data.duration_minutes * 60);
    } catch (error) {
      toast.error(error.message);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [schoolId, token, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!token) {
      navigate('/login');
      return;
    }
    fetchExam();
  }, [token, authLoading, navigate, fetchExam]);

  useEffect(() => {
    if (!exam || result) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmitRef.current?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [exam, result]);

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/exams/${schoolId}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          exam_id: schoolId,
          answers: answers
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit exam');
      }

      setResult(data);
      toast.success(data.passed ? 'Congratulations! You passed!' : 'Exam submitted');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  }, [schoolId, token, answers]);

  useEffect(() => { handleSubmitRef.current = handleSubmit; }, [handleSubmit]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Show loading while auth context is loading or exam data is loading
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#5f7c8a]" />
      </div>
    );
  }

  // Result Screen
  if (result) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        
        <main className="flex-1 pt-20 pb-12 px-4 flex items-center justify-center">
          <Card className="max-w-md w-full text-center">
            <CardHeader>
              <div className={`mx-auto p-4 rounded-full ${result.passed ? 'bg-green-100' : 'bg-red-100'} mb-4`}>
                {result.passed ? (
                  <Trophy size={48} className="text-green-600" />
                ) : (
                  <XCircle size={48} className="text-red-600" />
                )}
              </div>
              <CardTitle className="text-2xl">
                {result.passed ? 'Congratulations!' : 'Keep Learning!'}
              </CardTitle>
              <CardDescription>
                {result.passed ? 'You have passed the exam!' : 'You can retake this exam anytime.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-3xl font-bold text-[#5f7c8a]">{result.score.toFixed(0)}%</p>
                  <p className="text-sm text-gray-500">Score</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-3xl font-bold text-[#5f7c8a]">{result.correct_answers}/{result.total_questions}</p>
                  <p className="text-sm text-gray-500">Correct</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => navigate('/dashboard')}
                >
                  Back to Dashboard
                </Button>
                {!result.passed && (
                  <Button 
                    className="flex-1 bg-[#5f7c8a] hover:bg-[#4a6470]"
                    onClick={() => {
                      setResult(null);
                      setAnswers({});
                      setCurrentQuestion(0);
                      fetchExam();
                    }}
                  >
                    Retake Exam
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </main>

        <Footer />
      </div>
    );
  }

  const question = exam?.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / exam?.questions.length) * 100;
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-1 pt-20 pb-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Exam Header */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-800">{exam?.title}</h1>
                <p className="text-gray-500">{exam?.school_name}</p>
              </div>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${timeLeft < 300 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                <Clock size={20} />
                <span className="font-mono font-bold">{formatTime(timeLeft)}</span>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-500 mb-2">
                <span>Question {currentQuestion + 1} of {exam?.questions.length}</span>
                <span>{answeredCount} answered</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </div>

          {/* Question Card */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-start gap-4">
                <span className="bg-[#5f7c8a] text-white px-3 py-1 rounded-full text-sm font-medium">
                  Q{currentQuestion + 1}
                </span>
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide">
                    {question?.question_type === 'true_false' ? 'True/False' : 'Multiple Choice'}
                  </span>
                  <CardTitle className="text-lg mt-1">{question?.question_text}</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={answers[question?.id] || ''}
                onValueChange={(value) => handleAnswerChange(question?.id, value)}
                className="space-y-3"
              >
                {question?.options.map((option, index) => (
                  <div 
                    key={index}
                    className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                      answers[question?.id] === option 
                        ? 'border-[#5f7c8a] bg-[#5f7c8a]/5' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleAnswerChange(question?.id, option)}
                  >
                    <RadioGroupItem value={option} id={`option-${index}`} />
                    <Label htmlFor={`option-${index}`} className="flex-1 cursor-pointer">
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={() => setCurrentQuestion(prev => prev - 1)}
              disabled={currentQuestion === 0}
            >
              <ArrowLeft size={16} className="mr-2" /> Previous
            </Button>

            <div className="flex gap-2">
              {exam?.questions.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentQuestion(index)}
                  className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                    currentQuestion === index
                      ? 'bg-[#5f7c8a] text-white'
                      : answers[exam?.questions[index]?.id]
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>

            {currentQuestion === exam?.questions.length - 1 ? (
              <Button
                className="bg-[#5f7c8a] hover:bg-[#4a6470]"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                ) : (
                  <><CheckCircle size={16} className="mr-2" /> Submit Exam</>
                )}
              </Button>
            ) : (
              <Button
                onClick={() => setCurrentQuestion(prev => prev + 1)}
              >
                Next <ArrowRight size={16} className="ml-2" />
              </Button>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ExamPage;
